import { useFormik, FormikProvider, Field, Form as FormikForm } from "formik"
import { Button, Col, Form } from "react-bootstrap"
import * as Yup from "yup"
import styles from "./AccountManagement.module.scss"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { useContext, useState } from "react"
import { changeCustomerPassword } from "shared/routes/account/account-management"
import { TeacherPasswordChangeProps } from "shared/types/accountManagementTypes"
import { AuthContext } from "AuthContext"

const ChangePasswordPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isSSO } = useContext(AuthContext)

    function onSubmit (values: TeacherPasswordChangeProps) {
        setIsSubmitting(true)
        changeCustomerPassword(values)
            .then(() => {
                toast.success("You have updated password sucessfully.")
            })
            .catch(err => {
                toast.error(err?.response?.data ?? "There has been an error updating the information.")
            })
            .finally(() => {
                setIsSubmitting(false)
                formik.resetForm()
            })
    }

    const formik = useFormik({
        initialValues: {
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        },
        onSubmit,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                currentPassword: Yup
                    .string()
                    .label("Current Password")
                    .required("Current Password is required."),
                newPassword: Yup
                    .string()
                    .label("New Password")
                    .min(5, "Password must be 5 characters long")
                    .max(150)
                    .required("New Password is required."),
                confirmNewPassword: Yup
                    .string()
                    .label("Confirm Password")
                    .min(5, "Password must be 5 characters long")
                    .max(150)
                    .oneOf([Yup.ref("newPassword"), null], "Password you confirm need to match with new password.")
                    .required("Confirm Password is required."),
            })
    })

    if (isSubmitting) return <BeatLoader />

    if (isSSO) {
        return (
            <div className="w-75 mx-auto mt-5">
                <h5 className="border border-secondary rounded p-3">Your account is a Single Sign On account. You cannot change your password here.</h5>
            </div>
        )
    }

  return (
    <div>
        <h4 className="mt-4">Change Password</h4>
         <div className="d-flex align-items-center justify-content-center mt-4">
            <FormikProvider value={formik}>
                <Form as={FormikForm} className={`text-center ${styles.container} border border-1 border-secondary rounded p-3`}>
                    <Form.Group controlId="formCurrentPassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Current Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="currentPassword"
                            type="password"
                            placeholder="Current Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.currentPassword}
                            isInvalid={formik.touched.currentPassword && !!formik.errors.currentPassword}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.currentPassword}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formNewPassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>New Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="newPassword"
                            type="password"
                            placeholder="New Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.newPassword}
                            isInvalid={formik.touched.newPassword && !!formik.errors.newPassword}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.newPassword}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formConfirmNewPassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Confirm New Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="confirmNewPassword"
                            type="password"
                            placeholder="Confirm New Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.confirmNewPassword}
                            isInvalid={formik.touched.confirmNewPassword && !!formik.errors.confirmNewPassword}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.confirmNewPassword}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Button
                        className="rounded-lg px-4 py-2 mt-4"
                        variant="theme"
                        disabled={!formik.dirty || !formik.isValid || isSubmitting}
                        type="submit">
                            Change Password
                    </Button>
                </Form>
            </FormikProvider>
        </div>
    </div>
  )
}

export default ChangePasswordPage
